import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import styles from './UserVerify.module.css';

interface VerificationResponse {
  status: string;
  message: string;
  eventId?: string;
  data?: {
    verification_code: string;
    email: string;
    firstname: string;
    lastname: string;
    submit: string;
  };
}

export const UserVerify: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationState, setVerificationState] = useState<'init' | 'verifying' | 'success' | 'error'>('init');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setError('No token provided.');
      setLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        setVerificationState('verifying');
        
        const response = await apiClient.post<VerificationResponse>('/api/v2/web/verify/', { token });

        if (response.data.status === 'passed') {
          setVerificationState('success');
          
          // Kurze Pause für UX
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Erfolgreiche Verifikation - Weiterleitung vorbereiten
          const redirectBase = response.data.eventId 
            ? `/register/?event=${response.data.eventId}` 
            : '/events/';

          if (response.data.data?.verification_code && response.data.data?.email) {
            // Mit allen Parametern weiterleiten
            const params = new URLSearchParams({
              verification_code: response.data.data.verification_code,
              email: response.data.data.email,
              firstname: response.data.data.firstname,
              lastname: response.data.data.lastname,
              submit: response.data.data.submit
            });
            navigate(`${redirectBase}&${params.toString()}`, { replace: true });
          } else {
            // Einfache Weiterleitung
            navigate(redirectBase, { replace: true });
          }
        } else {
          setVerificationState('error');
          setError(response.data.message || 'Verification failed');
          setLoading(false);
        }
      } catch (error: any) {
        console.error('Verification error:', error);
        setVerificationState('error');
        setError('An unexpected error occurred. Please try again.');
        setLoading(false);
      }
    };

    verifyToken();
  }, [location.search, navigate]);

  return (
    <div className={styles.verificationContainer}>
      <div className={styles.container}>
        {loading ? (
          <>
            <div className={styles.icon}>
              {verificationState === 'init' && '🔐'}
              {verificationState === 'verifying' && '🔍'}
              {verificationState === 'success' && '✅'}
            </div>
            
            {verificationState === 'verifying' && (
              <>
                <h2 className={styles.title}>
                  Verifying your account...
                </h2>
                <p className={styles.description}>
                  Please wait while we check your verification token.
                </p>
              </>
            )}
            {verificationState === 'success' && (
              <>
                <h2 className={styles.title} style={{ color: '#28a745' }}>
                  Verification Successful!
                </h2>
                <p className={styles.description}>
                  Redirecting you to the application...
                </p>
              </>
            )}
            {verificationState === 'init' && (
              <>
                <h2 className={styles.title}>
                  Preparing verification...
                </h2>
                <p className={styles.description}>
                  Please wait...
                </p>
              </>
            )}
            <div className={styles.spinner}></div>
          </>
        ) : (
          <>
            <div className={styles.errorIcon}>❌</div>
            <h2 className={styles.errorTitle}>Verification Failed</h2>
            <p className={styles.errorMessage}>
              {error}
            </p>
            <button 
              className="btn btn-primary"
              style={{ width: '100%', maxWidth: '300px' }}
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default UserVerify;
